import * as React from 'react';

import {
  enumInventoryTagStatus,
  enumQuantityCaptureMethod,
  enumTagType,
  InventoryTagStatus,
  TagType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  NumberField,
  Select,
  TextField,
} from '@rematter/ui-web';
import { Col, SmartDatePicker } from '@rematter/ui-web/components';
import { useToast } from '@rematter/ui-web/hooks';
import { humanReadableShortPricingUnit } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import {
  createInventoryTagAtom,
  updateInventoryTagAtom,
} from '../../_graphql/mutationInventoryTag';
import { $remoteFacilities } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryTagRecord } from '../../types/inventoryTag';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectInventoryLot } from '~/shared/components/select-fields/SelectInventoryLot';
import {
  SelectMaterial,
  useMaterialsQuery,
} from '~/shared/components/select-fields/SelectMaterial';
import {
  maybeParseCalendarDate,
  maybeParseZonedDateTime,
  today,
  toISODateString,
} from '~/shared/utils/dateTime';

type InventoryTagDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingTag?: InventoryTagRecord | null;
};

export const InventoryTagDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingTag,
}: InventoryTagDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [tagNumber, setTagNumber] = React.useState('');
  const [tagType, setTagType] = React.useState<TagType>(
    enumTagType.FinishedGood,
  );
  const [status, setStatus] = React.useState<InventoryTagStatus>(
    enumInventoryTagStatus.Draft,
  );
  const [itemId, setItemId] = React.useState<string | undefined>(undefined);
  const [facilityId, setFacilityId] = React.useState('');
  const [lotId, setLotId] = React.useState<string | undefined>(undefined);
  const [generatedAt, setGeneratedAt] = React.useState<Date | null>(null);
  const [grossQuantity, setGrossQuantity] = React.useState<number | null>(null);
  const [tare, setTare] = React.useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const createTag = useSetAtom(createInventoryTagAtom({}));
  const updateTag = useSetAtom(updateInventoryTagAtom({}));

  const isEditMode = !!editingTag;
  const currentFacilityId = isEditMode
    ? (editingTag?.facilityId ?? '')
    : facilityId;

  const netQuantity = React.useMemo(() => {
    const gross = grossQuantity ?? 0;
    const tareValue = tare ?? 0;
    return Math.max(0, gross - tareValue);
  }, [grossQuantity, tare]);

  const { data: itemData } = useMaterialsQuery({
    ids: itemId ? [itemId] : undefined,
    queryOptions: { enabled: !!itemId },
  });

  const unitSuffix = React.useMemo(() => {
    const unit = itemData?.[0]?.measurementUnit;
    return humanReadableShortPricingUnit(unit) || undefined;
  }, [itemData]);

  React.useEffect(() => {
    if (editingTag) {
      setTagNumber(editingTag.tagNumber);
      setTagType(editingTag.tagType);
      setStatus(editingTag.status);
      setItemId(editingTag.itemId);
      setFacilityId(editingTag.facilityId);
      setLotId(editingTag.lotId);
      setGeneratedAt(
        maybeParseZonedDateTime(editingTag.generatedAt)?.toDate() ?? null,
      );
      setGrossQuantity(editingTag.grossQuantity);
      setTare(editingTag.tare);
    } else {
      setTagNumber('');
      setTagType(enumTagType.FinishedGood);
      setStatus(enumInventoryTagStatus.Draft);
      setItemId(undefined);
      setFacilityId('');
      setLotId(undefined);
      setGeneratedAt(today().toDate('UTC'));
      setGrossQuantity(null);
      setTare(0);
    }
  }, [editingTag]);

  const handleClose = React.useCallback(() => {
    setTagNumber('');
    setTagType(enumTagType.FinishedGood);
    setStatus(enumInventoryTagStatus.Draft);
    setItemId(undefined);
    setFacilityId('');
    setLotId(undefined);
    setGeneratedAt(null);
    setGrossQuantity(null);
    setTare(0);
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!itemId) {
      toast.error(t.tagsListView.dialog.validation.itemRequired);
      return;
    }

    if (!isEditMode && !facilityId) {
      toast.error(t.tagsListView.dialog.validation.facilityRequired);
      return;
    }

    if (!generatedAt) {
      toast.error(t.tagsListView.dialog.validation.generatedAtRequired);
      return;
    }

    if (grossQuantity === null) {
      toast.error(t.tagsListView.dialog.validation.grossQuantityRequired);
      return;
    }

    if (tare === null) {
      toast.error(t.tagsListView.dialog.validation.tareRequired);
      return;
    }

    try {
      setIsSubmitting(true);

      const generatedAtString = generatedAt.toISOString();

      if (isEditMode && editingTag) {
        await updateTag({
          id: editingTag.id,
          tagNumber: tagNumber.trim() || undefined,
          tagType,
          generatedAt: generatedAtString,
          status,
          grossQuantity,
          tare,
          itemId,
          lotId: lotId || null,
        });
        toast.success(t.tagsListView.dialog.updateSuccess);
      } else {
        await createTag({
          tagNumber: tagNumber.trim(),
          tagType,
          generatedAt: generatedAtString,
          status,
          grossQuantity,
          tare,
          itemId,
          facilityId,
          lotId: lotId || undefined,
          isRevised: false,
          quantityCaptureMethod: enumQuantityCaptureMethod.Manual,
          tareCaptureMethod: enumQuantityCaptureMethod.Manual,
        });
        toast.success(t.tagsListView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.tagsListView.dialog.unknownError;
      toast.error(
        isEditMode
          ? t.tagsListView.dialog.updateError(errorMessage)
          : t.tagsListView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    tagNumber,
    tagType,
    status,
    itemId,
    facilityId,
    lotId,
    generatedAt,
    grossQuantity,
    tare,
    isEditMode,
    editingTag,
    createTag,
    updateTag,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const tagTypeOptions = React.useMemo(
    () => [
      {
        key: enumTagType.WorkInProgress,
        label: t.tagsListView.tagTypes.WorkInProgress,
      },
      {
        key: enumTagType.FinishedGood,
        label: t.tagsListView.tagTypes.FinishedGood,
      },
    ],
    [t],
  );

  const statusOptions = React.useMemo(
    () => [
      {
        key: enumInventoryTagStatus.Draft,
        label: t.tagsListView.statusTypes.Draft,
      },
      {
        key: enumInventoryTagStatus.Active,
        label: t.tagsListView.statusTypes.Active,
      },
      {
        key: enumInventoryTagStatus.Confirmed,
        label: t.tagsListView.statusTypes.Confirmed,
      },
      {
        key: enumInventoryTagStatus.InInventory,
        label: t.tagsListView.statusTypes.InInventory,
      },
      {
        key: enumInventoryTagStatus.InTransfer,
        label: t.tagsListView.statusTypes.InTransfer,
      },
      {
        key: enumInventoryTagStatus.Loaded,
        label: t.tagsListView.statusTypes.Loaded,
      },
      {
        key: enumInventoryTagStatus.Reserved,
        label: t.tagsListView.statusTypes.Reserved,
      },
      {
        key: enumInventoryTagStatus.PendingPurchase,
        label: t.tagsListView.statusTypes.PendingPurchase,
      },
      {
        key: enumInventoryTagStatus.Shipped,
        label: t.tagsListView.statusTypes.Shipped,
      },
      {
        key: enumInventoryTagStatus.Sold,
        label: t.tagsListView.statusTypes.Sold,
      },
      {
        key: enumInventoryTagStatus.Transferred,
        label: t.tagsListView.statusTypes.Transferred,
      },
      {
        key: enumInventoryTagStatus.Revised,
        label: t.tagsListView.statusTypes.Revised,
      },
      {
        key: enumInventoryTagStatus.Cancelled,
        label: t.tagsListView.statusTypes.Cancelled,
      },
      {
        key: enumInventoryTagStatus.Voided,
        label: t.tagsListView.statusTypes.Voided,
      },
    ],
    [t],
  );

  const canSubmit =
    Boolean(itemId) &&
    (isEditMode || Boolean(facilityId)) &&
    Boolean(generatedAt) &&
    grossQuantity !== null &&
    tare !== null &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.tagsListView.dialog.editTitle
    : t.tagsListView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.tagsListView.dialog.updating
      : t.tagsListView.dialog.update
    : isSubmitting
      ? t.tagsListView.dialog.creating
      : t.tagsListView.dialog.create;

  return (
    <ConsoleDialog
      className="@InventoryTagDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <Col gap4>
          <TextField
            label={t.tagsListView.dialog.tagNumberLabel}
            value={tagNumber}
            onChange={setTagNumber}
            placeholder={t.tagsListView.dialog.tagNumberPlaceholder}
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label={t.tagsListView.dialog.tagTypeLabel}
              value={tagType}
              onChange={(key) => setTagType(key as TagType)}
              options={tagTypeOptions}
              isRequired
            />

            <Select
              label={t.tagsListView.dialog.statusLabel}
              value={status}
              onChange={(key) => setStatus(key as InventoryTagStatus)}
              options={statusOptions}
              isRequired
            />
          </div>

          <SelectMaterial
            label={t.tagsListView.dialog.itemLabel}
            value={itemId}
            onChange={(value) => setItemId(value)}
            placeholder={t.tagsListView.dialog.itemPlaceholder}
            isRequired
          />

          {!isEditMode && (
            <SelectFacility
              label={t.tagsListView.dialog.facilityLabel}
              value={facilityId}
              onChange={(value) => {
                setFacilityId(value ?? '');
                setLotId(undefined);
              }}
              placeholder={t.tagsListView.dialog.facilityPlaceholder}
              isRequired
            />
          )}

          {isEditMode && (
            <TextField
              label={t.tagsListView.dialog.facilityLabel}
              value={
                facilities.find((f) => f.id === editingTag?.facilityId)?.name ||
                editingTag?.facilityId ||
                ''
              }
              isDisabled
            />
          )}

          <SelectInventoryLot
            label={t.tagsListView.dialog.lotLabel}
            value={lotId}
            onChange={(value: string | undefined) => setLotId(value)}
            placeholder={t.tagsListView.dialog.lotPlaceholder}
            facilityId={currentFacilityId || undefined}
            skipSearch={!currentFacilityId}
            isDisabled={!currentFacilityId}
            clearable
          />

          <SmartDatePicker
            value={
              generatedAt
                ? maybeParseCalendarDate(generatedAt.toISOString())
                : null
            }
            label={t.tagsListView.dialog.generatedAtLabel}
            onChange={(date) =>
              setGeneratedAt(date ? new Date(toISODateString(date)) : null)
            }
            isRequired
          />

          <div className="grid grid-cols-3 gap-4">
            <NumberField
              label={t.tagsListView.dialog.grossQuantityLabel}
              value={grossQuantity ?? undefined}
              onChange={(value) => setGrossQuantity(value ?? null)}
              suffix={unitSuffix}
              showSteppers={false}
              isRequired
            />
            <NumberField
              label={t.tagsListView.dialog.tareLabel}
              value={tare ?? undefined}
              onChange={(value) => setTare(value ?? null)}
              suffix={unitSuffix}
              showSteppers={false}
              isRequired
            />
            <NumberField
              label={t.tagsListView.dialog.netQuantityLabel}
              value={netQuantity}
              suffix={unitSuffix}
              showSteppers={false}
              isDisabled
            />
          </div>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.tagsListView.dialog.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
